import { ProductStockRecord } from './stock.types';

export type StockInfoParams = {
  /** Record de `productStock` de la entidad. **Ausente = stock infinito** (no hay doc). */
  item?: ProductStockRecord;
  stockCheckDisabled: boolean;
};

export class StockInfo {
  item?: ProductStockRecord;
  totalStockInCart: number;
  reservedStockForOrder: number;
  stockCheckDisabled: boolean;

  private consumedStockByCartItemMap: Record<string, number>;

  constructor(params: StockInfoParams) {
    this.item = params.item;
    this.totalStockInCart = 0;
    this.reservedStockForOrder = 0;
    this.consumedStockByCartItemMap = {};
    this.stockCheckDisabled = params.stockCheckDisabled;
  }

  /**
   * Infinito = ausencia de record O record con `stockQty: null` (sin control). Tratar el `null` acá
   * (además de las fronteras API/venta) hace al lector defensivo ante un doc infinito-con-`null` que
   * llegue por un camino sin frontera (p.ej. el order-editor del admin lee `productStock` directo).
   */
  get hasUnlimitedStock(): boolean {
    if (this.stockCheckDisabled) {
      return true;
    }
    return !this.item || this.item.stockQty === null;
  }

  get availableStock(): number {
    if (this.hasUnlimitedStock) {
      return Number.MAX_SAFE_INTEGER;
    }
    // `hasUnlimitedStock` false ⇒ `item` presente y `stockQty` finito; el `?? 0` solo satisface a TS.
    return (this.item?.stockQty ?? 0) + this.reservedStockForOrder || 0;
  }

  get hasStockAvailable(): boolean {
    return this.availableStock > 0;
  }

  get missingStock(): number {
    if (this.availableStock < this.totalStockInCart) {
      return this.totalStockInCart - this.availableStock;
    }
    return 0;
  }

  get hasMissingStock(): boolean {
    return this.missingStock > 0;
  }

  get isOutOfStock(): boolean {
    return this.availableStock <= 0;
  }

  getMissingStockByCartItem(cartItemId: string): number {
    if (cartItemId) {
      if (this.availableStock < this.consumedStockByCartItemMap[cartItemId]) {
        return this.consumedStockByCartItemMap[cartItemId] - this.availableStock;
      }
    } else {
      if (this.availableStock < this.totalStockInCart) {
        return this.totalStockInCart - this.availableStock;
      }
    }
    return 0;
  }

  getConsumedStock(cartItemId?: string): number {
    if (!cartItemId) {
      return 0;
    }

    return this.consumedStockByCartItemMap[cartItemId] || 0;
  }

  setConsumedStock(consumedStock: number, cartItemId: string) {
    this.totalStockInCart += consumedStock;

    this.consumedStockByCartItemMap[cartItemId] =
      (this.consumedStockByCartItemMap[cartItemId] ?? 0) + consumedStock;
  }

  setReservedStock(reservedStock: number) {
    this.reservedStockForOrder += reservedStock;
  }
}
